import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import { useRootStore } from '@nandosaus/state-management';
import { H1, P, H2 } from '@components/typography/new';
import { HeadingWithLineHighlights } from '../heading-with-line-highlights';
import { HeadingWithFlameHighlights } from '../heading-with-flame-highlights';
import { ThreeLineHighlight } from '../three-line-highlight';
import { Container, Flex } from '../grid';
import { Image } from '../image';
import { cmsImagesPropTypes } from '../../prop-types';
import { useNavPadding } from '../../hooks/use-nav-padding';

const PageHeader = ({
  disabled,
  heading,
  image,
  children,
  description,
  eyebrowBackgroundColor,
  eyebrowTextColor,
  eyebrow,
  hasBottomCurve,
  tintOpacity,
  backgroundColor,
  minHeight,
  headingLevel,
  ...rest
}) => {
  const { UIStore } = useRootStore();

  // Set navbar theme based on whether page has an image
  useEffect(() => {
    if (image) {
      // Page has a hero image, navbar should be light (to contrast with dark overlay)
      const timer = setTimeout(() => {
        UIStore.setNavbarLight();
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [image, UIStore]);

  const { mobileNavPadding, desktopNavPadding } = useNavPadding();

  const imageHeadingLevel = heading?.length >= 35 ? H2 : H1;

  return (
    <Flex
      style={{ pointerEvents: disabled ? 'none' : 'auto' }}
      flexDirection="column"
      justifyContent="center"
      width="100%"
      backgroundColor={'transparent' || backgroundColor}
      position="relative"
      pb={image && { xs: '120px', lg: '80px' }}
      mb={!image && { xs: '40px', lg: '56px' }}
      pt={
        image
          ? { xs: `calc(48px + ${mobileNavPadding})`, lg: `calc(0px + ${desktopNavPadding})` }
          : { xs: `calc(32px + ${mobileNavPadding})`, lg: `calc(40px + ${desktopNavPadding})` }
      }
      minHeight={image && minHeight}
      {...rest}
    >
      {hasBottomCurve && image && (
        <Flex
          position="absolute"
          bottom="0"
          width="100%"
          backgroundColor="warmWhite2"
          borderRadius="32px 32px 0 0"
          height={{ xs: '24px', lg: '32px' }}
          zIndex={4}
        />
      )}
      {image && (
        <Flex
          zIndex={3}
          top="0"
          position="absolute"
          width="100%"
          height={{ xs: '90px', md: '150px' }}
          background="linear-gradient(to bottom, rgba(0, 0, 0, 0.8), transparent)"
        />
      )}
      {image && (
        <>
          <Flex
            zIndex={3}
            top="0"
            position="absolute"
            width="100%"
            height="100%"
            opacity={tintOpacity}
            backgroundColor="black"
          />
          <Image
            src={image?.url || image}
            alt={
              image?.alt ||
              'a Nando’s meal on a table including PERi-PERi chicken, a salad, chips, and a selection of sides'
            }
            focalPoint={image?.focalPoint}
            srcSet={image?.srcset}
            layout="fill"
            priority
            zIndex={2}
            quality={85}
            top="0"
            objectFit="cover"
            height="100%"
            width="100vw"
            position="absolute"
          />
        </>
      )}
      <Container zIndex={3} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        {typeof heading === 'string' ? (
          <Flex flexDirection="column" alignItems="center" position="relative" maxWidth="950px">
            {eyebrow && (
              <Flex position="absolute" top={image ? { lg: '-36px', xs: '-27px' } : '-32px'}>
                <HeadingWithFlameHighlights
                  zIndex={3}
                  heading={eyebrow}
                  variant="highlight-pill"
                  style={{ transform: !image && 'rotate(-3deg)' }}
                  mt="0"
                  mb="0"
                  pb="0"
                  color={eyebrowTextColor}
                  backgroundColor={eyebrowBackgroundColor}
                />
              </Flex>
            )}
            <Flex
              style={{
                transform: image ? 'rotate(-3deg)' : 'rotate(0deg)',
              }}
            >
              <HeadingWithLineHighlights
                heading={heading}
                headingLevel={image ? imageHeadingLevel : H2}
                highlightBackgroundColor="black"
                mb="0"
                mt="0"
                pb="0"
                highlightColor="white"
                overrideHeadingHighlighting={image && ['highlightAllLines']}
              />
            </Flex>
          </Flex>
        ) : (
          <ThreeLineHighlight
            isAnimated={false}
            headingLevel={image ? headingLevel || H1 : H2}
            heading={heading}
            eyeBrow={eyebrow}
            eyebrowBackgroundColor={eyebrowBackgroundColor}
            eyebrowTextColor={eyebrowTextColor}
          />
        )}
        {(description || children) && (
          <Flex width="100%" pb={image ? { xs: '26px', lg: '32px' } : { xs: '20px', lg: '24px' }} />
        )}

        {description && (
          <Flex maxWidth={image ? '724px' : '1142px'}>
            <P color={image ? 'white' : 'black'} variant={image ? 'medium-bold' : 'medium'}>
              {description}
            </P>
          </Flex>
        )}
        {children}
      </Container>
    </Flex>
  );
};
PageHeader.propTypes = {
  disabled: PropTypes.bool,
  heading: PropTypes.oneOfType([
    PropTypes.string, // CMS button array format
    PropTypes.arrayOf(PropTypes.string), // Direct button object format
  ]),
  image: cmsImagesPropTypes,
  children: PropTypes.node,
  description: PropTypes.string,
  eyebrow: PropTypes.string,
  eyebrowBackgroundColor: PropTypes.string,
  eyebrowTextColor: PropTypes.string,
  hasBottomCurve: PropTypes.bool,
  tintOpacity: PropTypes.number,
  backgroundColor: PropTypes.string,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  headingLevel: PropTypes.node,
};

PageHeader.defaultProps = {
  disabled: false,
  heading: '',
  image: false,
  children: null,
  description: '',
  eyebrow: '',
  eyebrowBackgroundColor: 'black',
  eyebrowTextColor: 'white',
  hasBottomCurve: true,
  tintOpacity: 0.3,
  backgroundColor: 'white',
  minHeight: { xs: '540px', lg: '720px' },
  headingLevel: null,
};

const ComposedPageHeader = observer(PageHeader);
export { ComposedPageHeader as PageHeader };
